<script lang="ts">
  /**
   * ConflictResolutionModal.svelte — Conflict resolution UI
   *
   * Shows when file-level conflicts are detected during hydration.
   * Presents a side-by-side diff of local vs remote content with
   * three resolution options: keep local, use remote, or manual merge.
   */

  import { i18n } from '../../stores/i18nStore';
  import {
    syncConflicts,
    conflictCount,
    resolveConflict,
    clearResolvedConflicts,
  } from '../../services/gitSyncService';
  import type { SyncConflict } from '../../services/gitSyncService';

  /** Currently viewed conflict (null = modal closed) */
  let activeConflict: SyncConflict | null = null;

  /** Manual merge content (editable) */
  let manualContent = '';

  /** Whether the manual merge editor is open */
  let showManualEditor = false;

  /** Loading state during resolution */
  let resolving = false;

  /** Open the modal for a specific conflict */
  function openConflict(conflict: SyncConflict) {
    activeConflict = conflict;
    manualContent = conflict.localContent;
    showManualEditor = false;
    resolving = false;
  }

  /** Close the modal */
  function closeModal() {
    activeConflict = null;
    showManualEditor = false;
    resolving = false;
  }

  /** Resolve with keep local */
  async function keepLocal() {
    if (!activeConflict || resolving) return;
    resolving = true;
    await resolveConflict(activeConflict.id, 'keep_local');
    resolving = false;
    activeConflict = null;
  }

  /** Resolve with use remote */
  async function useRemote() {
    if (!activeConflict || resolving) return;
    resolving = true;
    await resolveConflict(activeConflict.id, 'use_remote');
    resolving = false;
    activeConflict = null;
  }

  /** Resolve with manual merge */
  async function applyManual() {
    if (!activeConflict || resolving) return;
    resolving = true;
    await resolveConflict(activeConflict.id, 'manual', manualContent);
    resolving = false;
    activeConflict = null;
  }

  /** Toggle the manual editor */
  function toggleManualEditor() {
    showManualEditor = !showManualEditor;
    if (showManualEditor && activeConflict) {
      manualContent = activeConflict.localContent;
    }
  }

  /** Extract filename from a full path */
  function filename(path: string): string {
    return path.split('/').pop() || path;
  }

  /** Get unresolved conflicts */
  $: unresolvedConflicts = $syncConflicts.filter(c => !c.resolved);
</script>

<!-- Conflict badge trigger (shown in header area) -->
{#if $conflictCount > 0}
  <button
    class="conflict-badge"
    on:click={() => unresolvedConflicts.length > 0 && openConflict(unresolvedConflicts[0])}
    title={$i18n('sync.conflictsDetected')}
  >
    <span class="badge-icon">!</span>
    <span class="badge-count">{$conflictCount}</span>
  </button>
{/if}

<!-- Modal overlay -->
{#if activeConflict}
  <div class="modal-overlay" on:click|self={closeModal}>
    <div class="modal">
      <div class="modal-header">
        <h2>{$i18n('sync.conflictTitle')}</h2>
        <button class="btn-close" on:click={closeModal}>&times;</button>
      </div>

      <div class="modal-meta">
        <span class="file-path">{filename(activeConflict.filePath)}</span>
        <span class="conflict-time">
          {new Date(activeConflict.detectedAt).toLocaleString()}
        </span>
      </div>

      {#if !showManualEditor}
        <!-- Side-by-side diff view -->
        <div class="diff-container">
          <div class="diff-pane">
            <h3 class="pane-title local">{$i18n('sync.localVersion')}</h3>
            <pre class="diff-content">{activeConflict.localContent}</pre>
          </div>
          <div class="diff-separator"></div>
          <div class="diff-pane">
            <h3 class="pane-title remote">{$i18n('sync.remoteVersion')}</h3>
            <pre class="diff-content">{activeConflict.remoteContent}</pre>
          </div>
        </div>
      {:else}
        <!-- Manual merge editor -->
        <div class="manual-editor">
          <h3 class="pane-title">{$i18n('sync.manualMerge')}</h3>
          <textarea
            class="merge-textarea"
            bind:value={manualContent}
            spellcheck="false"
          ></textarea>
        </div>
      {/if}

      <div class="modal-actions">
        <button
          class="btn btn-local"
          on:click={keepLocal}
          disabled={resolving}
        >
          {$i18n('sync.keepLocal')}
        </button>
        <button
          class="btn btn-remote"
          on:click={useRemote}
          disabled={resolving}
        >
          {$i18n('sync.useRemote')}
        </button>
        {#if showManualEditor}
          <button
            class="btn btn-manual"
            on:click={applyManual}
            disabled={resolving}
          >
            {$i18n('sync.applyMerge')}
          </button>
        {/if}
        <button
          class="btn btn-toggle"
          on:click={toggleManualEditor}
          disabled={resolving}
        >
          {showManualEditor ? $i18n('sync.showDiff') : $i18n('sync.manualMerge')}
        </button>
      </div>

      <!-- Conflict list (if multiple) -->
      {#if unresolvedConflicts.length > 1}
        <div class="conflict-list">
          <h4>{$i18n('sync.otherConflicts')}</h4>
          {#each unresolvedConflicts as conflict}
            {#if conflict.id !== activeConflict.id}
              <button
                class="conflict-item"
                on:click={() => openConflict(conflict)}
              >
                {filename(conflict.filePath)}
              </button>
            {/if}
          {/each}
        </div>
      {/if}
    </div>
  </div>
{/if}

<style>
  .conflict-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    background: var(--error-color, #dc2626);
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s;
  }

  .conflict-badge:hover {
    opacity: 0.85;
  }

  .badge-icon {
    font-weight: 700;
  }

  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .modal {
    background: var(--panel-background, #fff);
    border-radius: 8px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    width: min(90vw, 1000px);
    max-height: 85vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid var(--border-color, #e5e7eb);
  }

  .modal-header h2 {
    margin: 0;
    font-size: 1.1rem;
    color: var(--error-color, #dc2626);
  }

  .btn-close {
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: var(--text-muted, #6b7280);
    line-height: 1;
  }

  .modal-meta {
    display: flex;
    justify-content: space-between;
    padding: 8px 20px;
    font-size: 0.85rem;
    color: var(--text-muted, #6b7280);
    border-bottom: 1px solid var(--border-color, #e5e7eb);
  }

  .file-path {
    font-family: monospace;
    font-weight: 500;
  }

  .diff-container {
    display: flex;
    flex: 1;
    overflow: hidden;
    min-height: 300px;
  }

  .diff-pane {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .diff-separator {
    width: 1px;
    background: var(--border-color, #e5e7eb);
    flex-shrink: 0;
  }

  .pane-title {
    padding: 8px 12px;
    margin: 0;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid var(--border-color, #e5e7eb);
  }

  .pane-title.local {
    color: var(--accent-color, #3b82f6);
    background: rgba(59, 130, 246, 0.05);
  }

  .pane-title.remote {
    color: var(--success-color, #16a34a);
    background: rgba(22, 163, 106, 0.05);
  }

  .diff-content {
    flex: 1;
    margin: 0;
    padding: 12px;
    overflow: auto;
    font-family: 'SF Mono', 'Fira Code', monospace;
    font-size: 0.8rem;
    line-height: 1.6;
    white-space: pre-wrap;
    word-wrap: break-word;
    background: var(--panel-background, #fff);
  }

  .manual-editor {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 300px;
  }

  .merge-textarea {
    flex: 1;
    margin: 0;
    padding: 12px;
    border: none;
    outline: none;
    resize: none;
    font-family: 'SF Mono', 'Fira Code', monospace;
    font-size: 0.8rem;
    line-height: 1.6;
    background: var(--panel-background, #fff);
    color: var(--text-primary, #1f2937);
  }

  .modal-actions {
    display: flex;
    gap: 8px;
    padding: 12px 20px;
    border-top: 1px solid var(--border-color, #e5e7eb);
    justify-content: flex-end;
  }

  .btn {
    padding: 6px 16px;
    border: 1px solid var(--border-color, #e5e7eb);
    border-radius: 6px;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .btn-local {
    background: var(--accent-color, #3b82f6);
    color: white;
    border-color: var(--accent-color, #3b82f6);
  }

  .btn-local:hover:not(:disabled) {
    opacity: 0.9;
  }

  .btn-remote {
    background: var(--success-color, #16a34a);
    color: white;
    border-color: var(--success-color, #16a34a);
  }

  .btn-remote:hover:not(:disabled) {
    opacity: 0.9;
  }

  .btn-manual {
    background: var(--warning-color, #d97706);
    color: white;
    border-color: var(--warning-color, #d97706);
  }

  .btn-manual:hover:not(:disabled) {
    opacity: 0.9;
  }

  .btn-toggle {
    background: transparent;
    color: var(--text-muted, #6b7280);
  }

  .btn-toggle:hover:not(:disabled) {
    background: var(--hover-background, #f9fafb);
    color: var(--text-primary, #1f2937);
  }

  .conflict-list {
    padding: 8px 20px 12px;
    border-top: 1px solid var(--border-color, #e5e7eb);
  }

  .conflict-list h4 {
    margin: 0 0 6px;
    font-size: 0.8rem;
    color: var(--text-muted, #6b7280);
  }

  .conflict-item {
    display: block;
    padding: 4px 8px;
    margin: 2px 0;
    background: transparent;
    border: 1px solid var(--border-color, #e5e7eb);
    border-radius: 4px;
    font-family: monospace;
    font-size: 0.8rem;
    cursor: pointer;
    color: var(--text-primary, #1f2937);
    text-align: left;
  }

  .conflict-item:hover {
    background: var(--hover-background, #f9fafb);
    border-color: var(--error-color, #dc2626);
  }
</style>
